import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSearch,
    faRefresh,
    faShoppingCart,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faUser,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faBuilding,
    faGlobe,
    faUserTag,
    faStickyNote,
    faIdCard,
    faSave,
    faBan,
    faUserEdit,
    faImage,
    faHashtag,
    faUserPlus,
    faExternalLinkAlt,
    faLink,
    faStore,
    faExchangeAlt,
    faBox,
    faDollarSign,
    faMoneyBillWave,
    faCreditCard,
    faWallet,
    faUniversity,
    faReceipt,
    faHistory,
    faCalculator,
    faBalanceScale,
    faPercent,
    faInfoCircle,
    faRulerCombined,
    faLayerGroup,
    faClock,
    faFileInvoice,
    faUsers,
    faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance
const createPosOrderListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in POS Orders List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        ordersList: 'POS Orders List',
        shopOrders: 'Shop Orders',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        searchPlaceholder: 'Search orders in shop...',
        refresh: 'Refresh',
        loadingOrders: 'Loading shop orders...',
        noOrdersFound: 'No orders found in shop',
        orderDetails: 'Order Details',
        close: 'Close',
        serialNo: 'S.No',
        date: 'Date',
        orderNo: 'Order No',
        invoiceNo: 'Invoice No',
        customerName: 'Customer',
        phone: 'Phone',
        items: 'Items',
        grandTotal: 'Total',
        paidAmount: 'Paid',
        balanceDue: 'Balance',
        paymentMethod: 'Payment',
        orderStatus: 'Status',
        actions: 'Actions',
        addedBy: 'Added By',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        print: 'Print',
        deletedSuccess: 'Order deleted successfully',
        deleteError: 'Error deleting order',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected. Please select a shop first.',
        orderId: 'Order ID',
        customerDetails: 'Customer Details',
        notProvided: 'Not provided',
        pending: 'Pending',
        completed: 'Completed',
        cancelled: 'Cancelled',
        cash: 'Cash',
        card: 'Card',
        bankTransfer: 'Bank Transfer',
        easyPaisa: 'EasyPaisa',
        jazzCash: 'JazzCash',
        other: 'Other',
        showing: 'Showing',
        of: 'of',
        orders: 'orders',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        pendingFilter: 'Pending',
        completedFilter: 'Completed',
        cancelledFilter: 'Cancelled',
        filterBy: 'Filter By',
        orderNoFilter: 'Order Number',
        invoiceNoFilter: 'Invoice Number',
        customerFilter: 'Customer Name',
        phoneFilter: 'Phone',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        exportToPDF: 'PDF',
        addedByFilter: 'Added By',
        editOrder: 'Edit Order',
        updateOrder: 'Update Order',
        updating: 'Updating...',
        orderUpdated: 'Order updated successfully',
        updateError: 'Error updating order',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        dateRequired: 'Date is required',
        orderNoRequired: 'Order number is required',
        grandTotalRequired: 'Grand total is required',
        paidRequired: 'Paid amount is required',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this order?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        loadingUserData: 'Loading shop orders data...',
        noUserRecords: 'No orders found in your shop',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalOrders: 'Total Orders',
        totalPaid: 'Total Paid',
        totalBalance: 'Total Balance',
        totalAmount: 'Total Amount',
        orderInfo: 'Order Information',
        orderDate: 'Order Date',
        orderTime: 'Order Time',
        employee: 'Employee',
        itemsList: 'Order Items',
        productName: 'Product',
        batchNo: 'Batch',
        unit: 'Unit',
        price: 'Price',
        quantity: 'Qty',
        total: 'Total',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        shipping: 'Shipping',
        notes: 'Notes',
        optional: 'Optional',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        selectShop: 'Please select a shop to view orders',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        invoice: 'INVOICE',
        receipt: 'RECEIPT',
        thankYou: 'Thank you for your business!',
        poweredBy: 'Powered by OurZest',
        cashier: 'Cashier',
        time: 'Time',
        transactionId: 'Transaction ID',
        contactUs: 'Contact Us',
        pageSize: 'Page Size',
        a4: 'A4',
        a5: 'A5',
        thermal: 'Thermal (80mm)',
        printing: 'Printing...',
        printReceipt: 'Print Receipt',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        status: 'Status',
        employeeName: 'Employee',
        orderStatusOptions: {
            pending: 'Pending',
            completed: 'Completed',
            cancelled: 'Cancelled'
        }
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const posOrderListTranslator = createPosOrderListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// Print Settings Modal Component
const PrintSettingsModal = ({ isOpen, onClose, order, businessInfo, shopInfo, translations, onPrint }) => {
    const [pageSize, setPageSize] = useState('thermal');
    const [orientation, setOrientation] = useState('portrait');
    const [isPrinting, setIsPrinting] = useState(false);

    if (!isOpen || !order) return null;

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return new Date().toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = (timeString) => {
        if (timeString) {
            return timeString;
        }
        return new Date().toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    const handlePrint = async () => {
        setIsPrinting(true);
        
        try {
            const shopName = shopInfo?.name || businessInfo?.businessname || 'Shop Name';
            const shopAddress = businessInfo?.businessaddress || '';
            const shopPhone = businessInfo?.phonenumber || '';
            
            const subtotal = order?.subtotal || 
                order?.items?.reduce((sum, item) => sum + (parseFloat(item.price) * parseFloat(item.quantity)), 0).toFixed(2) || '0.00';
            const tax = order?.tax || '0.00';
            const discount = order?.discount || '0.00';
            const shipping = order?.shipping || '0.00';
            const grandTotal = order?.grand_total || (parseFloat(subtotal) + parseFloat(tax) + parseFloat(shipping) - parseFloat(discount)).toFixed(2);
            const paidAmount = order?.amount_paid || grandTotal;
            const balance = order?.balance_due || (parseFloat(grandTotal) - parseFloat(paidAmount)).toFixed(2);
            
            const isThermal = pageSize === 'thermal';
            const pageWidth = isThermal ? '80mm' : (pageSize === 'a5' ? '148mm' : '210mm');
            
            const receiptHTML = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Order ${order.order_no}</title>
                    <style>
                        @page {
                            size: ${isThermal ? '80mm auto' : `${pageSize} ${orientation}`};
                            margin: ${isThermal ? '2mm' : '5mm'};
                        }
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        body {
                            font-family: ${isThermal ? "'Courier New', monospace" : "'Inter', 'Segoe UI', sans-serif"};
                            background: white;
                            color: black;
                            width: ${pageWidth};
                            margin: 0 auto;
                            padding: ${isThermal ? '3mm' : '10px'};
                            font-size: ${isThermal ? '9pt' : '11pt'};
                        }
                        .receipt-container {
                            width: 100%;
                        }
                        .text-center { text-align: center; }
                        .text-right { text-align: right; }
                        .text-left { text-align: left; }
                        .font-bold { font-weight: bold; }
                        .border-bottom { border-bottom: 1px dashed #000; }
                        .border-top { border-top: 1px dashed #000; }
                        .border-solid { border-bottom: 1px solid #000; }
                        .mb-1 { margin-bottom: 2mm; }
                        .mb-2 { margin-bottom: 4mm; }
                        .mt-1 { margin-top: 2mm; }
                        .mt-2 { margin-top: 4mm; }
                        .pt-1 { padding-top: 1mm; }
                        .pb-1 { padding-bottom: 1mm; }
                        table { 
                            width: 100%; 
                            border-collapse: collapse;
                            font-size: ${isThermal ? '8pt' : '10pt'};
                        }
                        th { 
                            text-align: left; 
                            border-bottom: 1px solid #000;
                            padding: ${isThermal ? '2px 0' : '4px 0'};
                        }
                        td { 
                            padding: ${isThermal ? '2px 0' : '4px 0'};
                        }
                        .shop-name {
                            font-size: ${isThermal ? '14pt' : '18pt'};
                            font-weight: bold;
                            margin-bottom: 2mm;
                        }
                        .invoice-title {
                            font-size: ${isThermal ? '12pt' : '16pt'};
                            font-weight: bold;
                            text-transform: uppercase;
                            margin: 2mm 0;
                        }
                        .contact-line {
                            font-size: ${isThermal ? '7pt' : '9pt'};
                            margin-top: 1mm;
                        }
                        .totals-row {
                            display: flex;
                            justify-content: space-between;
                            padding: 2px 0;
                        }
                        .grand-total {
                            font-size: ${isThermal ? '11pt' : '14pt'};
                            font-weight: bold;
                            border-top: 1px solid #000;
                            margin-top: 2mm;
                            padding-top: 2mm;
                        }
                        .footer {
                            text-align: center;
                            font-size: ${isThermal ? '7pt' : '9pt'};
                            margin-top: 4mm;
                            padding-top: 2mm;
                            border-top: 1px dashed #000;
                        }
                        .contact-numbers {
                            font-size: ${isThermal ? '8pt' : '10pt'};
                            font-weight: bold;
                            margin: 2mm 0;
                        }
                        .status-badge {
                            display: inline-block;
                            padding: 2px 8px;
                            border-radius: 4px;
                            font-size: ${isThermal ? '7pt' : '9pt'};
                            font-weight: bold;
                        }
                        .status-completed { background: #d1fae5; color: #065f46; }
                        .status-pending { background: #fed7aa; color: #92400e; }
                        .status-cancelled { background: #fee2e2; color: #991b1b; }
                        @media print {
                            body { margin: 0; padding: ${isThermal ? '2mm' : '5mm'}; }
                        }
                    </style>
                </head>
                <body>
                    <div class="receipt-container">
                        <!-- Header -->
                        <div class="text-center">
                            <div class="shop-name">${shopName}</div>
                            ${shopAddress ? `<div>${shopAddress}</div>` : ''}
                            ${shopPhone ? `<div>Tel: ${shopPhone}</div>` : ''}
                            <div class="contact-numbers">
                                📞 03150559988  |  📞 03158000062
                            </div>
                        </div>

                        <!-- Title -->
                        <div class="text-center invoice-title">
                            ${translations?.invoice || 'ORDER INVOICE'}
                        </div>

                        <!-- Order Info -->
                        <div style="display: flex; justify-content: space-between; margin-bottom: 2mm;">
                            <span>${translations?.orderNo || 'Order No'}: ${order.order_no}</span>
                            <span>${translations?.invoiceNo || 'Invoice No'}: ${order.invoice_no}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; margin-bottom: 3mm;">
                            <span>${translations?.date || 'Date'}: ${formatDate(order.order_date)}</span>
                            <span>${translations?.time || 'Time'}: ${formatTime(order.order_time)}</span>
                        </div>

                        <!-- Customer Info -->
                        ${(order.customer_name || order.customer_phone) ? `
                        <div class="border-top border-bottom pt-1 pb-1 mb-2">
                            ${order.customer_name ? `<div><strong>${order.customer_name}</strong></div>` : ''}
                            ${order.customer_phone ? `<div>${translations?.phone || 'Phone'}: ${order.customer_phone}</div>` : ''}
                            ${order.customer_address ? `<div>${translations?.address || 'Address'}: ${order.customer_address}</div>` : ''}
                        </div>
                        ` : ''}

                        <!-- Employee Info -->
                        ${order.employee_name ? `
                        <div style="margin-bottom: 2mm;">
                            <div><strong>${translations?.employee || 'Employee'}:</strong> ${order.employee_name}</div>
                        </div>
                        ` : ''}

                        <!-- Items Table -->
                        <table>
                            <thead>
                                <tr>
                                    <th>${translations?.productName || 'Item'}</th>
                                    <th class="text-center">${translations?.quantity || 'Qty'}</th>
                                    <th class="text-right">${translations?.price || 'Price'}</th>
                                    <th class="text-right">${translations?.total || 'Total'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${order.items?.map(item => `
                                    <tr>
                                        <td>${item.product_name}${item.batch_no ? `<div style="font-size: 7pt;">Batch: ${item.batch_no}</div>` : ''}</td>
                                        <td class="text-center">${parseFloat(item.quantity).toFixed(2)}</td>
                                        <td class="text-right">${parseFloat(item.price).toFixed(2)}</td>
                                        <td class="text-right">${parseFloat(item.total || item.price * item.quantity).toFixed(2)}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>

                        <!-- Totals -->
                        <div class="border-top pt-1 mt-1">
                            <div class="totals-row">
                                <span>${translations?.subtotal || 'Subtotal'}:</span>
                                <span>Rs. ${parseFloat(subtotal).toFixed(2)}</span>
                            </div>
                            ${parseFloat(tax) > 0 ? `
                            <div class="totals-row">
                                <span>${translations?.tax || 'Tax'}:</span>
                                <span>Rs. ${parseFloat(tax).toFixed(2)}</span>
                            </div>
                            ` : ''}
                            ${parseFloat(discount) > 0 ? `
                            <div class="totals-row">
                                <span>${translations?.discount || 'Discount'}:</span>
                                <span>-Rs. ${parseFloat(discount).toFixed(2)}</span>
                            </div>
                            ` : ''}
                            ${parseFloat(shipping) > 0 ? `
                            <div class="totals-row">
                                <span>${translations?.shipping || 'Shipping'}:</span>
                                <span>Rs. ${parseFloat(shipping).toFixed(2)}</span>
                            </div>
                            ` : ''}
                            <div class="totals-row grand-total">
                                <span>${translations?.grandTotal || 'Grand Total'}:</span>
                                <span>Rs. ${parseFloat(grandTotal).toFixed(2)}</span>
                            </div>
                            <div class="totals-row">
                                <span>${translations?.paidAmount || 'Paid'}:</span>
                                <span>Rs. ${parseFloat(paidAmount).toFixed(2)}</span>
                            </div>
                            <div class="totals-row">
                                <span>${translations?.balanceDue || 'Balance'}:</span>
                                <span>Rs. ${parseFloat(balance).toFixed(2)}</span>
                            </div>
                        </div>

                        <!-- Payment Info -->
                        <div class="text-center" style="margin: 2mm 0;">
                            ${translations?.paymentMethod || 'Payment'}: ${order.payment_method?.toUpperCase() || 'CASH'}
                        </div>

                        <div class="text-center">
                            <span class="status-badge status-${order.order_status}">
                                ${translations?.orderStatusOptions?.[order.order_status] || order.order_status}
                            </span>
                        </div>

                        <!-- Notes -->
                        ${order.notes ? `
                        <div class="text-center" style="font-size: 7pt; margin: 2mm 0;">
                            ${order.notes}
                        </div>
                        ` : ''}

                        <!-- Footer -->
                        <div class="footer">
                            <div style="font-weight: bold; margin-bottom: 1mm;">
                                ${translations?.thankYou || 'Thank you for your business!'}
                            </div>
                            <div>${translations?.poweredBy || 'Powered by OurZest'}</div>
                            <div class="contact-line">For Support: 03150559988 | 03158000062</div>
                        </div>
                    </div>
                </body>
                </html>
            `;
            
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write(receiptHTML);
                printWindow.document.close();
                
                printWindow.onload = () => {
                    printWindow.print();
                    printWindow.onafterprint = () => {
                        printWindow.close();
                    };
                };
            }
            
            if (onPrint) onPrint();
            onClose();
        } catch (error) {
            console.error('Print error:', error);
        } finally {
            setIsPrinting(false);
        }
    };

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern print-settings-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                <div className="modal-header-modern">
                    <div className="modal-icon">
                        <FontAwesomeIcon icon={faPrint} />
                    </div>
                    <div className="modal-title">
                        <h3>{translations?.printReceipt || 'Print Receipt'}</h3>
                    </div>
                    <button className="close-btn-modern" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body-modern" style={{ padding: '20px' }}>
                    <div className="form-group-modern" style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            <FontAwesomeIcon icon={faRulerCombined} style={{ marginRight: '8px' }} />
                            {translations?.pageSize || 'Page Size'}:
                        </label>
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                border: '1px solid #ddd',
                                fontSize: '14px'
                            }}
                        >
                            <option value="a4">{translations?.a4 || 'A4'}</option>
                            <option value="a5">{translations?.a5 || 'A5'}</option>
                            <option value="thermal">{translations?.thermal || 'Thermal (80mm)'}</option>
                        </select>
                    </div>

                    {pageSize !== 'thermal' && (
                        <div className="form-group-modern" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                                <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '8px' }} />
                                {translations?.orientation || 'Orientation'}:
                            </label>
                            <select
                                value={orientation}
                                onChange={(e) => setOrientation(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="portrait">{translations?.portrait || 'Portrait'}</option>
                                <option value="landscape">{translations?.landscape || 'Landscape'}</option>
                            </select>
                        </div>
                    )}

                    <div className="print-preview-info" style={{
                        background: '#f8f9fa',
                        padding: '12px',
                        borderRadius: '8px',
                        marginTop: '15px',
                        fontSize: '13px',
                        color: '#666'
                    }}>
                        <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px' }} />
                        {pageSize === 'thermal' 
                            ? 'Printing on thermal paper (80mm width). Best for receipts.'
                            : `Printing on ${pageSize.toUpperCase()} paper in ${orientation} orientation.`}
                    </div>
                </div>
                <div className="modal-footer-modern" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '15px', borderTop: '1px solid #eee' }}>
                    <button
                        className="btn-modern btn-secondary-modern"
                        onClick={onClose}
                        disabled={isPrinting}
                    >
                        <FontAwesomeIcon icon={faTimes} style={{ marginRight: '5px' }} />
                        {translations?.cancel || 'Cancel'}
                    </button>
                    <button
                        className="btn-modern btn-primary-modern"
                        onClick={handlePrint}
                        disabled={isPrinting}
                        style={{ background: '#10b981' }}
                    >
                        {isPrinting ? (
                            <>
                                <div className="btn-spinner" style={{ marginRight: '8px' }}></div>
                                {translations?.printing || 'Printing...'}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faPrint} style={{ marginRight: '5px' }} />
                                {translations?.print || 'Print'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const PosOrderList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        phonenumber: '',
        fullname: '',
        email: ''
    });

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr'
    });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        id: '',
        order_date: '',
        order_time: '',
        order_no: '',
        invoice_no: '',
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        employee_name: '',
        subtotal: '',
        tax: '',
        discount: '',
        shipping: '',
        grand_total: '',
        amount_paid: '',
        balance_due: '',
        payment_method: 'cash',
        order_status: 'pending',
        notes: ''
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#10b981';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Enhanced filter states
    const [filterType, setFilterType] = useState('all');
    const [filterBy, setFilterBy] = useState('all');
    const [orderNoFilter, setOrderNoFilter] = useState('');
    const [invoiceNoFilter, setInvoiceNoFilter] = useState('');
    const [customerFilter, setCustomerFilter] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Calculate totals
    const totalOrders = filteredOrders.length;
    const totalGrandTotal = filteredOrders.reduce((sum, order) => {
        return sum + (parseFloat(order.grand_total) || 0);
    }, 0);
    const totalPaid = filteredOrders.reduce((sum, order) => {
        return sum + (parseFloat(order.amount_paid) || 0);
    }, 0);
    const totalBalance = filteredOrders.reduce((sum, order) => {
        return sum + (parseFloat(order.balance_due) || 0);
    }, 0);

    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentOrders = filteredOrders.slice(startIndex, endIndex);

    // Function to show toast
    const showToast = (type, message) => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction
        });

        setTimeout(() => {
            setToast(prev => ({ ...prev, show: false }));
        }, 3000);
    };

    // Fetch business info
    const fetchBusinessInfo = async () => {
        if (!user?.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    businessaddress: data.businessaddress || '',
                    phonenumber: data.phonenumber || '',
                    fullname: data.fullname || '',
                    email: data.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
        }
    };

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                document.documentElement.style.setProperty('--customer-primary-color', color);
                document.documentElement.style.setProperty('--customer-primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--customer-primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#10b981';
            setCurrentTheme(theme);
            applyTheme(theme);
        };

        window.addEventListener('storage', handleThemeChange);
        return () => window.removeEventListener('storage', handleThemeChange);
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await posOrderListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [currentLanguage]);

    // Fetch orders when shop changes
    useEffect(() => {
        if (currentShop) {
            fetchOrders();
            fetchAvailableShops();
            fetchBusinessInfo();
        } else {
            setLoading(false);
        }
    }, [currentShop]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [orders, searchTerm, filterType, filterBy, orderNoFilter, invoiceNoFilter, customerFilter, phoneFilter, addedByFilter, startDate, endDate]);

    // Fetch available shops
    const fetchAvailableShops = async () => {
        if (!user) return;

        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }

        return (
            <div className="shop-indicator-modern active">
                <div className="shop-badge">
                    <FontAwesomeIcon icon={faStore} />
                    <div className="shop-details">
                        <span className="shop-name">{currentShop.shop_name}</span>
                        <span className="shop-code">({currentShop.shop_code || currentShop.shop_id})</span>
                    </div>
                </div>
                <div className="shop-info-note">
                    {translations.shopOrders || 'Showing orders from this shop'}
                </div>
                <button
                    className="btn-modern btn-switch-shop"
                    onClick={() => setShowShopSelector(true)}
                >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <span>{translations.switchShop || 'Switch Shop'}</span>
                </button>
            </div>
        );
    };

    const fetchOrders = async () => {
        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pos_orders.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_orders',
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setOrders(data.data || []);

                if (data.data.length === 0) {
                    showToast('info', translations.noUserRecords || 'No orders found in your shop');
                }
            } else {
                showToast('error', data.message || translations.networkError || 'Network error occurred');
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...orders];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(order =>
                order.order_no?.toLowerCase().includes(term) ||
                order.invoice_no?.toLowerCase().includes(term) ||
                order.customer_name?.toLowerCase().includes(term) ||
                order.customer_phone?.toLowerCase().includes(term) ||
                order.username?.toLowerCase().includes(term)
            );
        }

        if (filterType !== 'all') {
            filtered = filtered.filter(order => order.order_status === filterType);
        }

        if (filterBy === 'orderNo' && orderNoFilter.trim()) {
            const term = orderNoFilter.toLowerCase();
            filtered = filtered.filter(order =>
                order.order_no?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'invoiceNo' && invoiceNoFilter.trim()) {
            const term = invoiceNoFilter.toLowerCase();
            filtered = filtered.filter(order =>
                order.invoice_no?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'customer' && customerFilter.trim()) {
            const term = customerFilter.toLowerCase();
            filtered = filtered.filter(order =>
                order.customer_name?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'phone' && phoneFilter.trim()) {
            const term = phoneFilter.toLowerCase();
            filtered = filtered.filter(order =>
                order.customer_phone?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(order =>
                order.username?.toLowerCase().includes(term)
            );
        }

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(order => {
                if (!order.order_date) return false;
                const orderDate = new Date(order.order_date);
                orderDate.setHours(0, 0, 0, 0);
                return orderDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(order => {
                if (!order.order_date) return false;
                const orderDate = new Date(order.order_date);
                return orderDate <= end;
            });
        }

        setFilteredOrders(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setOrderNoFilter('');
        setInvoiceNoFilter('');
        setCustomerFilter('');
        setPhoneFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        setFilterType('all');
    };

    const handleView = async (order) => {
        setSelectedOrder(order);
        
        if (!order.items && order.id && currentShop) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/pos_orders.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: order.id,
                        shop_id: currentShop.shop_id
                    })
                });

                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    setSelectedOrder(data.data);
                }
            } catch (error) {
                console.error('Error fetching order details:', error);
            }
        }
        
        setShowViewModal(true);
    };

    const handlePrint = (order) => {
        setSelectedOrder(order);
        setShowPrintModal(true);
    };

    const handleEdit = (order) => {
        setSelectedOrder(order);
        
        const formattedDate = order.order_date ? order.order_date.split('T')[0] : '';
        
        setEditFormData({
            id: order.id,
            order_date: formattedDate,
            order_time: order.order_time || '',
            order_no: order.order_no || '',
            invoice_no: order.invoice_no || '',
            customer_name: order.customer_name || '',
            customer_phone: order.customer_phone || '',
            customer_address: order.customer_address || '',
            employee_name: order.employee_name || '',
            subtotal: order.subtotal || '',
            tax: order.tax || '',
            discount: order.discount || '',
            shipping: order.shipping || '',
            grand_total: order.grand_total || '',
            amount_paid: order.amount_paid || '',
            balance_due: order.balance_due || '',
            payment_method: order.payment_method || 'cash',
            order_status: order.order_status || 'pending',
            notes: order.notes || ''
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (orderId) => {
        setOrderToDelete(orderId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!orderToDelete || !currentShop) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pos_orders.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'delete',
                    id: orderToDelete,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Order deleted successfully');
                fetchOrders();
                setShowDeleteModal(false);
                setOrderToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting order');
            }
        } catch (error) {
            console.error('Error deleting order:', error);
            showToast('error', translations.deleteError || 'Error deleting order');
        }
    };

    const validateEditForm = () => {
        const newErrors = {};

        if (!editFormData.order_date) {
            newErrors.order_date = translations.dateRequired || 'Date is required';
        }

        if (!editFormData.order_no.trim()) {
            newErrors.order_no = translations.orderNoRequired || 'Order number is required';
        }

        if (!editFormData.grand_total || parseFloat(editFormData.grand_total) <= 0) {
            newErrors.grand_total = translations.grandTotalRequired || 'Grand total is required';
        }

        if (editFormData.amount_paid === '' || editFormData.amount_paid === null) {
            newErrors.amount_paid = translations.paidRequired || 'Paid amount is required';
        }

        if (editFormData.amount_paid && editFormData.grand_total && parseFloat(editFormData.amount_paid) > parseFloat(editFormData.grand_total)) {
            newErrors.amount_paid = 'Paid amount cannot exceed grand total';
        }

        setEditFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        
        let updatedFormData = { ...editFormData, [name]: value };

        if (name === 'grand_total' || name === 'amount_paid') {
            const grandTotal = parseFloat(name === 'grand_total' ? value : editFormData.grand_total) || 0;
            const paid = parseFloat(name === 'amount_paid' ? value : editFormData.amount_paid) || 0;
            updatedFormData.balance_due = (grandTotal - paid).toFixed(2);
        }

        setEditFormData(updatedFormData);

        if (editFormErrors[name]) {
            setEditFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateOrder = async (e) => {
        e.preventDefault();

        if (!selectedOrder || !currentShop) return;

        if (!validateEditForm()) {
            return;
        }

        setEditLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pos_orders.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'update',
                    id: selectedOrder.id,
                    order_status: editFormData.order_status,
                    order_date: editFormData.order_date,
                    amount_paid: editFormData.amount_paid,
                    balance_due: editFormData.balance_due,
                    payment_method: editFormData.payment_method,
                    notes: editFormData.notes,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.orderUpdated || 'Order updated successfully');
                fetchOrders();
                setShowEditModal(false);
                setSelectedOrder(null);
                setEditFormErrors({});
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating order');
            }
        } catch (error) {
            console.error('Error updating order:', error);
            showToast('error', translations.updateError || 'Error updating order');
        } finally {
            setEditLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const formatDateGlobal = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '-';
        return `Rs. ${parseFloat(amount).toFixed(2)}`;
    };

    const getOrderStatusBadge = (status) => {
        const statusConfig = {
            pending: { color: '#f59e0b', bg: '#fef3c7', text: translations.pending || 'Pending' },
            completed: { color: '#10b981', bg: '#d1fae5', text: translations.completed || 'Completed' },
            cancelled: { color: '#ef4444', bg: '#fee2e2', text: translations.cancelled || 'Cancelled' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return (
            <div className="order-status-badge" style={{ 
                backgroundColor: config.bg,
                color: config.color,
                border: `1px solid ${config.color}40`,
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '500',
                display: 'inline-block'
            }}>
                {config.text}
            </div>
        );
    };

    const getPaymentMethodIcon = (method) => {
        switch (method) {
            case 'cash': return faMoneyBillWave;
            case 'card': return faCreditCard;
            case 'bankTransfer': return faUniversity;
            case 'easyPaisa': return faWallet;
            case 'jazzCash': return faWallet;
            default: return faDollarSign;
        }
    };

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Print Settings Modal */}
            <PrintSettingsModal
                isOpen={showPrintModal}
                onClose={() => setShowPrintModal(false)}
                order={selectedOrder}
                businessInfo={businessInfo}
                shopInfo={currentShop}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faClipboardList} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.ordersList || 'POS Orders List'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : translations.shopOrders || 'Shop Orders'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Summary Stats */}
            <div className="summary-stats-modern">
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                        <FontAwesomeIcon icon={faClipboardList} style={{ color: '#10b981' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totalOrders}</div>
                        <div className="stat-label">{translations.totalOrders || 'Total Orders'}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                        <FontAwesomeIcon icon={faDollarSign} style={{ color: '#3b82f6' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalGrandTotal)}</div>
                        <div className="stat-label">{translations.totalAmount || 'Total Amount'}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                        <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#f59e0b' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalPaid)}</div>
                        <div className="stat-label">{translations.totalPaid || 'Total Paid'}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ef4444' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalBalance)}</div>
                        <div className="stat-label">{translations.totalBalance || 'Total Balance'}</div>
                    </div>
                </div>
            </div>

            {/* Advanced Filters Section */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    {/* Filter By Dropdown */}
                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.filterBy || 'Filter By'}</option>
                            <option value="orderNo">{translations.orderNoFilter || 'Order Number'}</option>
                            <option value="invoiceNo">{translations.invoiceNoFilter || 'Invoice Number'}</option>
                            <option value="customer">{translations.customerFilter || 'Customer Name'}</option>
                            <option value="phone">{translations.phone || 'Phone'}</option>
                            <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                            <option value="date">{translations.dateRange || 'Date Range'}</option>
                        </select>
                    </div>

                    {filterBy === 'orderNo' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={orderNoFilter}
                                onChange={(e) => setOrderNoFilter(e.target.value)}
                                placeholder={translations.orderNoFilter || 'Order number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'invoiceNo' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={invoiceNoFilter}
                                onChange={(e) => setInvoiceNoFilter(e.target.value)}
                                placeholder={translations.invoiceNoFilter || 'Invoice number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'customer' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={customerFilter}
                                onChange={(e) => setCustomerFilter(e.target.value)}
                                placeholder={translations.customerFilter || 'Customer name...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'phone' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={phoneFilter}
                                onChange={(e) => setPhoneFilter(e.target.value)}
                                placeholder={translations.phone || 'Phone number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'added_by' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={addedByFilter}
                                onChange={(e) => setAddedByFilter(e.target.value)}
                                placeholder={translations.addedByFilter || 'Added by username...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'date' && (
                        <>
                            <div className="filter-group compact date-filter">
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.startDate || 'Start Date'}
                                />
                            </div>
                            <div className="filter-group compact date-filter">
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.endDate || 'End Date'}
                                />
                            </div>
                        </>
                    )}

                    {/* Order Status Filter */}
                    <div className="filter-group compact">
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.orderStatus || 'Order Status'}</option>
                            <option value="pending">{translations.pendingFilter || 'Pending'}</option>
                            <option value="completed">{translations.completedFilter || 'Completed'}</option>
                            <option value="cancelled">{translations.cancelledFilter || 'Cancelled'}</option>
                        </select>
                    </div>

                    {/* Search Box */}
                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search orders in shop...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    {/* Refresh Button */}
                    <div className="filter-group compact">
                        <button
                            onClick={fetchOrders}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingUserData || 'Loading shop orders data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view orders'}</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.date || 'Date'}</th>
                                            <th className="centered-cell">{translations.orderNo || 'Order No'}</th>
                                            <th className="centered-cell">{translations.invoiceNo || 'Invoice No'}</th>
                                            <th className="centered-cell">{translations.customerName || 'Customer'}</th>
                                            <th className="centered-cell">{translations.items || 'Items'}</th>
                                            <th className="centered-cell">{translations.grandTotal || 'Total'}</th>
                                            <th className="centered-cell">{translations.paidAmount || 'Paid'}</th>
                                            <th className="centered-cell">{translations.balanceDue || 'Balance'}</th>
                                            <th className="centered-cell">{translations.orderStatus || 'Status'}</th>
                                            <th className="centered-cell">{translations.paymentMethod || 'Payment'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentOrders.map((order, index) => {
                                            const itemCount = order.items ? order.items.length : (order.item_count || '—');
                                            const addedBy = order.username || '-';
                                            
                                            return (
                                                <tr key={order.id}>
                                                    <td className="id-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faHashtag} className="cell-icon" />
                                                            <span className="cell-text">{startIndex + index + 1}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faCalendar} className="cell-icon" />
                                                            <span className="cell-text">{formatDateGlobal(order.order_date)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faFileInvoice} className="cell-icon" />
                                                            <span className="cell-text" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{order.order_no}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faReceipt} className="cell-icon" />
                                                            <span className="cell-text" style={{ fontFamily: 'monospace' }}>{order.invoice_no}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faUser} className="cell-icon" />
                                                            <span className="cell-text">{order.customer_name || (translations.walkInCustomer || 'Walk-in')}</span>
                                                            {order.customer_phone && (
                                                                <div className="cell-subtext">
                                                                    <FontAwesomeIcon icon={faPhone} size="xs" />
                                                                    <span>{order.customer_phone}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="items-badge">
                                                                <FontAwesomeIcon icon={faBox} />
                                                                <span>{itemCount}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="grand-total" style={{ color: '#059669', fontWeight: 'bold' }}>
                                                                {formatCurrency(order.grand_total)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="paid-amount" style={{ color: '#3b82f6', fontWeight: 'bold' }}>
                                                                {formatCurrency(order.amount_paid)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className={`balance-amount ${parseFloat(order.balance_due) > 0 ? 'has-balance' : 'no-balance'}`} 
                                                                  style={{ 
                                                                      color: parseFloat(order.balance_due) > 0 ? '#d97706' : '#059669',
                                                                      fontWeight: 'bold'
                                                                  }}>
                                                                {formatCurrency(order.balance_due)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            {getOrderStatusBadge(order.order_status)}
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <div className="payment-method">
                                                                <FontAwesomeIcon icon={getPaymentMethodIcon(order.payment_method)} />
                                                                <span>{translations[order.payment_method] || order.payment_method}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className={`added-by-badge ${addedBy === user?.username ? 'self' : ''}`}>
                                                                <FontAwesomeIcon icon={faUser} className="cell-icon" size="xs" />
                                                                <span className="cell-text">{addedBy}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="cell-content centered-content actions-content">
                                                            <button
                                                                onClick={() => handleView(order)}
                                                                className="action-btn-modern view-btn-modern"
                                                                title={translations.view || 'View'}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button
                                                                onClick={() => handlePrint(order)}
                                                                className="action-btn-modern print-btn-modern"
                                                                title={translations.print || 'Print'}
                                                            >
                                                                <FontAwesomeIcon icon={faPrint} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(order)}
                                                                className="action-btn-modern edit-btn-modern"
                                                                title={translations.edit || 'Edit'}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(order.id)}
                                                                className="action-btn-modern delete-btn-modern"
                                                                title={translations.delete || 'Delete'}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* No Data Message */}
                                {currentOrders.length === 0 && !loading && currentShop && (
                                    <div className="no-data-modern">
                                        <FontAwesomeIcon icon={faClipboardList} />
                                        <p>{translations.noUserRecords || 'No orders found in your shop'}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredOrders.length > 0 && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredOrders.length)} {translations.of || 'of'} {filteredOrders.length} {translations.orders || 'orders'}</span>
                                {currentShop?.shop_name && (
                                    <span className="shop-info-pagination">
                                        | {translations.shopName || 'Shop'}: {currentShop.shop_name}
                                    </span>
                                )}
                                <span style={{ marginLeft: '20px', color: '#059669', fontWeight: '600' }}>
                                    {translations.totalAmount || 'Total Amount'}: {formatCurrency(totalGrandTotal)}
                                </span>
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedOrder && (
                <div className="modal-overlay-modern" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faReceipt} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.orderDetails || 'Order Details'}</h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            <div className="detail-section">
                                <h4 className="section-title">{translations.orderInfo || 'Order Information'}</h4>
                                <div className="details-grid">
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.orderId || 'Order ID'}:</span>
                                        <span className="detail-value">#{selectedOrder.id}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.orderNo || 'Order No'}:</span>
                                        <span className="detail-value" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{selectedOrder.order_no}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.invoiceNo || 'Invoice No'}:</span>
                                        <span className="detail-value">{selectedOrder.invoice_no}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.date || 'Date'}:</span>
                                        <span className="detail-value">{formatDateGlobal(selectedOrder.order_date)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.time || 'Time'}:</span>
                                        <span className="detail-value">{selectedOrder.order_time || '-'}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.employee || 'Employee'}:</span>
                                        <span className="detail-value">{selectedOrder.employee_name || '-'}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.customerName || 'Customer Name'}:</span>
                                        <span className="detail-value">{selectedOrder.customer_name || (translations.walkInCustomer || 'Walk-in Customer')}</span>
                                    </div>
                                    {selectedOrder.customer_phone && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">{translations.phone || 'Phone'}:</span>
                                            <span className="detail-value">{selectedOrder.customer_phone}</span>
                                        </div>
                                    )}
                                    {selectedOrder.customer_address && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">{translations.customerAddress || 'Address'}:</span>
                                            <span className="detail-value">{selectedOrder.customer_address}</span>
                                        </div>
                                    )}
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.paymentMethod || 'Payment Method'}:</span>
                                        <span className="detail-value">
                                            <FontAwesomeIcon icon={getPaymentMethodIcon(selectedOrder.payment_method)} />
                                            {translations[selectedOrder.payment_method] || selectedOrder.payment_method}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.orderStatus || 'Order Status'}:</span>
                                        <span className="detail-value">
                                            {getOrderStatusBadge(selectedOrder.order_status)}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.grandTotal || 'Grand Total'}:</span>
                                        <span className="detail-value" style={{ color: '#059669', fontWeight: 'bold' }}>{formatCurrency(selectedOrder.grand_total)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.paidAmount || 'Paid Amount'}:</span>
                                        <span className="detail-value" style={{ color: '#3b82f6', fontWeight: 'bold' }}>{formatCurrency(selectedOrder.amount_paid)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.balanceDue || 'Balance'}:</span>
                                        <span className="detail-value" style={{ color: parseFloat(selectedOrder.balance_due) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }}>
                                            {formatCurrency(selectedOrder.balance_due)}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.addedBy || 'Added By'}:</span>
                                        <span className="detail-value">
                                            <span className="added-by-badge" style={{ background: '#e0e7ff', color: '#4f46e5' }}>
                                                <FontAwesomeIcon icon={faUser} size="xs" />
                                                {selectedOrder.username || '-'}
                                            </span>
                                        </span>
                                    </div>
                                    {selectedOrder.notes && (
                                        <div className="detail-row-modern full-width">
                                            <span className="detail-label">{translations.notes || 'Notes'}:</span>
                                            <span className="detail-value notes-text">{selectedOrder.notes}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Items Section */}
                            {selectedOrder.items && selectedOrder.items.length > 0 && (
                                <div className="detail-section">
                                    <h4 className="section-title">{translations.itemsList || 'Order Items'}</h4>
                                    <div className="items-table-container">
                                        <table className="items-table">
                                            <thead>
                                                <tr>
                                                    <th>{translations.serialNo || '#'}</th>
                                                    <th>{translations.productName || 'Product'}</th>
                                                    <th>{translations.batchNo || 'Batch'}</th>
                                                    <th>{translations.unit || 'Unit'}</th>
                                                    <th>{translations.price || 'Price'}</th>
                                                    <th>{translations.quantity || 'Qty'}</th>
                                                    <th>{translations.total || 'Total'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedOrder.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{item.product_name}</td>
                                                        <td className="batch-cell">{item.batch_no || '-'}</td>
                                                        <td>{item.unit || '-'}</td>
                                                        <td className="amount-cell">{formatCurrency(item.price)}</td>
                                                        <td className="amount-cell">{parseFloat(item.quantity).toFixed(2)}</td>
                                                        <td className="amount-cell total">{formatCurrency(item.total)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="6" className="text-right">{translations.subtotal || 'Subtotal'}:</td>
                                                    <td className="amount-cell">{formatCurrency(selectedOrder.subtotal)}</td>
                                                </tr>
                                                {parseFloat(selectedOrder.tax) > 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-right">{translations.tax || 'Tax'}:</td>
                                                        <td className="amount-cell">{formatCurrency(selectedOrder.tax)}</td>
                                                    </tr>
                                                )}
                                                {parseFloat(selectedOrder.discount) > 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-right">{translations.discount || 'Discount'}:</td>
                                                        <td className="amount-cell">-{formatCurrency(selectedOrder.discount)}</td>
                                                    </tr>
                                                )}
                                                {parseFloat(selectedOrder.shipping) > 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-right">{translations.shipping || 'Shipping'}:</td>
                                                        <td className="amount-cell">{formatCurrency(selectedOrder.shipping)}</td>
                                                    </tr>
                                                )}
                                                <tr className="grand-total-row">
                                                    <td colSpan="6" className="text-right">{translations.grandTotal || 'Grand Total'}:</td>
                                                    <td className="amount-cell grand-total">{formatCurrency(selectedOrder.grand_total)}</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" className="text-right">{translations.paidAmount || 'Paid'}:</td>
                                                    <td className="amount-cell paid">{formatCurrency(selectedOrder.amount_paid)}</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" className="text-right">{translations.balanceDue || 'Balance'}:</td>
                                                    <td className="amount-cell balance">{formatCurrency(selectedOrder.balance_due)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                {translations.close || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedOrder && (
                <div className="modal-overlay-modern edit-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content-modern edit-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="form-header-modern edit-form-header">
                            <div className="header-content">
                                <div className="header-icon">
                                    <FontAwesomeIcon icon={faUserEdit} />
                                </div>
                                <div className="header-text">
                                    <h1>{translations.editOrder || 'Edit Order'}</h1>
                                    <p>{translations.fillDetails || 'Fill in the order details below'}</p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleUpdateOrder} className="modern-form edit-form">
                            <div className="form-grid-modern edit-form-grid">
                                <div className={`form-group-modern ${editFormErrors.order_date ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                                        <input
                                            type="date"
                                            id="order_date"
                                            name="order_date"
                                            value={editFormData.order_date}
                                            onChange={handleEditFormChange}
                                            required
                                            max={getTodayDate()}
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        <label htmlFor="order_date" className="input-label">
                                            {translations.date || 'Date'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.order_date && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.order_date}
                                        </div>
                                    )}
                                </div>

                                <div className={`form-group-modern ${editFormErrors.order_no ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                        <input
                                            type="text"
                                            id="order_no"
                                            name="order_no"
                                            value={editFormData.order_no}
                                            readOnly
                                            className="readonly-input"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label htmlFor="order_no" className="input-label">
                                            {translations.orderNo || 'Order Number'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.order_no && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.order_no}
                                        </div>
                                    )}
                                </div>

                                <div className={`form-group-modern ${editFormErrors.grand_total ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faDollarSign} className="input-icon" />
                                        <input
                                            type="number"
                                            id="grand_total"
                                            name="grand_total"
                                            value={editFormData.grand_total}
                                            onChange={handleEditFormChange}
                                            required
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        <label htmlFor="grand_total" className="input-label">
                                            {translations.grandTotal || 'Grand Total'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.grand_total && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.grand_total}
                                        </div>
                                    )}
                                </div>

                                <div className={`form-group-modern ${editFormErrors.amount_paid ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                        <input
                                            type="number"
                                            id="amount_paid"
                                            name="amount_paid"
                                            value={editFormData.amount_paid}
                                            onChange={handleEditFormChange}
                                            required
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        <label htmlFor="amount_paid" className="input-label">
                                            {translations.paidAmount || 'Paid Amount'}
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.amount_paid && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.amount_paid}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                        <select
                                            id="payment_method"
                                            name="payment_method"
                                            value={editFormData.payment_method}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            <option value="cash">{translations.cash || 'Cash'}</option>
                                            <option value="card">{translations.card || 'Card'}</option>
                                            <option value="bankTransfer">{translations.bankTransfer || 'Bank Transfer'}</option>
                                            <option value="easyPaisa">{translations.easyPaisa || 'EasyPaisa'}</option>
                                            <option value="jazzCash">{translations.jazzCash || 'JazzCash'}</option>
                                            <option value="other">{translations.other || 'Other'}</option>
                                        </select>
                                        <label htmlFor="payment_method" className="input-label">
                                            {translations.paymentMethod || 'Payment Method'}
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faCheckCircle} className="input-icon" />
                                        <select
                                            id="order_status"
                                            name="order_status"
                                            value={editFormData.order_status}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            <option value="pending">{translations.pending || 'Pending'}</option>
                                            <option value="completed">{translations.completed || 'Completed'}</option>
                                            <option value="cancelled">{translations.cancelled || 'Cancelled'}</option>
                                        </select>
                                        <label htmlFor="order_status" className="input-label">
                                            {translations.orderStatus || 'Order Status'}
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="calculation-display">
                                        <div className="calculation-item">
                                            <span className="calculation-label">
                                                <FontAwesomeIcon icon={faCalculator} />
                                                {translations.balanceDue || 'Balance'}:
                                            </span>
                                            <span className={`calculation-value ${parseFloat(editFormData.balance_due) > 0 ? 'has-balance' : 'no-balance'}`}
                                                  style={{ color: parseFloat(editFormData.balance_due) > 0 ? '#d97706' : '#059669' }}>
                                                Rs. {parseFloat(editFormData.balance_due || 0).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="form-group-modern full-width">
                                <div className="input-container">
                                    <FontAwesomeIcon icon={faStickyNote} className="input-icon" />
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        value={editFormData.notes}
                                        onChange={handleEditFormChange}
                                        rows="2"
                                        placeholder={translations.notes || 'Enter any additional notes'}
                                        disabled={editLoading}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="notes" className="input-label">
                                        {translations.notes || 'Notes'}
                                        <span className="optional">{translations.optional || ' Optional'}</span>
                                    </label>
                                </div>
                            </div>

                            <div className="form-actions-modern edit-form-actions">
                                <button
                                    type="button"
                                    className="btn-modern btn-secondary-modern"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={editLoading}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    <span>{translations.cancel || 'Cancel'}</span>
                                </button>
                                <button
                                    type="submit"
                                    className="btn-modern btn-primary-modern"
                                    disabled={editLoading}
                                >
                                    {editLoading ? (
                                        <>
                                            <div className="btn-spinner"></div>
                                            <span>{translations.updating || 'Updating...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>{translations.saveChanges || 'Save Changes'}</span>
                                            <FontAwesomeIcon icon={faSave} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        <div className="edit-customer-info">
                            <p><strong>{translations.orderId || 'Order ID'}:</strong> #{selectedOrder.id}</p>
                            <p><strong>{translations.orderNo || 'Order No'}:</strong> {selectedOrder.order_no}</p>
                            <p><strong>{translations.shopName || 'Shop'}:</strong> {currentShop?.shop_name || 'N/A'}</p>
                        </div>

                        {editLoading && (
                            <div className="loading-overlay edit-loading-overlay">
                                <div className="loading-content">
                                    <div className="loading-spinner"></div>
                                    <p>{translations.updating || 'Updating...'}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div className="modal-title">
                                <h3 style={{ color: 'var(--customer-error-color)' }}>
                                    {translations.confirmDelete || 'Confirm Delete'}
                                </h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>
                                    {translations.deleteMessage || 'Are you sure you want to delete this order?'}
                                </p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                                    {translations.deleteWarning || 'This action cannot be undone.'}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                type="button"
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.noCancel || 'No, Cancel'}
                            </button>
                            <button
                                type="button"
                                className="btn-modern btn-delete-modern"
                                onClick={handleDelete}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                {translations.yesDelete || 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PosOrderList;